import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { ChevronDown } from 'lucide-react';
import { Fragment, ReactNode, useState, useMemo, useEffect, useRef } from 'react';

interface SelectOption {
  value: string | undefined;
  label: string | ReactNode;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value: string | undefined;
  onChange: (value: string | number) => void;
  label?: string | ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  noResultsText?: string;
}

export const Select = ({
  options,
  value,
  onChange,
  label,
  placeholder = 'Выберите опцию',
  disabled = false,
  className = '',
  searchable = false,
  searchPlaceholder = 'Поиск...',
  noResultsText = 'Ничего не найдено',
}: SelectProps) => {
  const [query, setQuery] = useState('');
  const [displayValue, setDisplayValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false); // Добавляем состояние для управления открытием
  const comboboxInputRef = useRef<HTMLInputElement>(null);

  // Находим выбранную опцию
  const selectedOption = useMemo(() => 
    options.find((opt) => opt.value === value),
    [options, value]
  );

  // Фильтрация опций по запросу
  const filteredOptions = query === '' ? options : options.filter((i) => i.label?.toString().toLowerCase().includes(query.toLowerCase()));

  // Обработчик выбора опции
  const handleSelect = (selectedValue: string | null) => {
    onChange(String(selectedValue));
    if (searchable) {
      // Находим лейбл выбранной опции для отображения
      const selected = options.find(opt => opt.value === selectedValue);
      if (selected) {
        const displayText = typeof selected.label === 'string' 
          ? selected.label 
          : String(selected.label);
        setDisplayValue(displayText);
      }
      setQuery(''); // Сбрасываем поисковый запрос
    }
  };

  // Обработчик изменения ввода (только для searchable)
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setQuery(newValue);
    setDisplayValue(newValue);
    
    // Если поле очищено, сбрасываем значение
    if (newValue === '' && searchable) {
      onChange('');
    }
    
    // Автоматически открываем список при вводе
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Обработчик фокуса на инпуте
  const handleInputFocus = () => {
    if (!disabled && !isOpen) {
      setIsOpen(true);
    }
  };

  // Обработчик клика на кнопку стрелочки
  const handleButtonClick = () => {
    setIsOpen(!isOpen);
  };

  // Обработчик закрытия списка
  const handleClose = () => {
    setIsOpen(false);
  };

   // Закрытие списка при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isOpen && !target.closest('[data-combobox]')) {
        setIsOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  // Синхронизация displayValue при изменении value извне
  useEffect(() => {
    if (selectedOption) {
      const displayText = typeof selectedOption.label === 'string' 
        ? selectedOption.label 
        : String(selectedOption.label);
      setDisplayValue(displayText);
    } else if (!searchable) {
      setDisplayValue('');
    }
  }, [selectedOption, searchable]);

  // Для не-searchable версии используем обычный Listbox
  if (!searchable) {
    return (
      <Listbox value={value}  onChange={onChange} disabled={disabled}>
      {({ open }) => (
        <div className={`relative ${className}`}>
          {label && (
            <ListboxLabel className="block text-sm font-medium text-gray-700 mb-1">
              {label}
            </ListboxLabel>
          )}
          <ListboxButton className="relative w-full flex cursor-default h-full !rounded-[12px] bg-white !pl-[10px] items-center !pr-[7px] !py-[0] text-left border-[0.51px] ring-0 border-[#54545657] focus:outline-none  sm:text-sm">
            <span className={`flex items-center text-center w-full truncate ${!selectedOption ? 'text-[#333333]' : ''}`}>
              {selectedOption?.label || placeholder}
            </span>
            <span className="pointer-events-none  right-0 flex items-center">
              <ChevronDown
                className=" text-[#333333] self-center"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-[12px] bg-white py-1 text-base ring-[0.51px] ring-[#333333] !border-0  focus:ring-0  focus:outline-none sm:text-sm">
              {options.map((option) => (
                <ListboxOption
                  key={option.value}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-8 pr-4 ${
                      active ? 'bg-blue-50 text-blue-900' : 'text-[#333333]'
                    } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`
                  }
                  value={option.value}
                  disabled={option.disabled}
                  title={typeof option.label === 'string' ? option.label : undefined}
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {option.label}
                      </span>

                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-1.5 text-blue-600">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      )}
    </Listbox>
    );
  }

  // Для searchable версии используем Combobox с поиском
  return (
    <Combobox 
      value={value} 
      onChange={handleSelect}
      disabled={disabled}
      onClose={handleClose} // Добавляем обработчик закрытия
    >
      <div className={`relative ${className}`} data-combobox>
        {label && (
          <label className="block text-sm font-medium text-[#232323] mb-1">
            {label}
          </label>
        )}
        
        <div className="relative">
          <ComboboxInput
            ref={comboboxInputRef}
            className="w-full rounded-[12px] border-[0.51px] border-[#54545657] bg-white py-2 px-3 text-[#232323]  placeholder:text-[#232323]  ring-0 focus-visible::ring-[#54545657]   sm:text-sm"
            placeholder={searchPlaceholder}
            displayValue={() => value ? displayValue : ''}
            onChange={handleInputChange}
            onFocus={handleInputFocus} // Добавляем обработчик фокуса
            autoComplete="off"
          />
          
          <div className="absolute inset-y-0 right-0 flex items-center">
            <ComboboxButton 
              className="!pr-2"
              onClick={handleButtonClick} // Добавляем обработчик клика
            >
              <ChevronDown
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
          </div>
        </div>

        <Transition
          show={isOpen} // Используем наше состояние для управления видимостью
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => {
            setQuery('');
            handleClose();
          }}
        >
          <ComboboxOptions 
            static
            className="absolute z-10 mt-[2px] max-h-60 w-full overflow-auto rounded-[12px] bg-white py-1 text-base border-[0.51px] border-[#54545657] ring-opacity-5 focus:outline-none sm:text-sm"
          >
            {filteredOptions.length === 0 && query !== '' ? (
              <div className="relative cursor-default select-none py-2 px-4 text-[#232323]">
                {noResultsText}
              </div>
            ) : (
              filteredOptions.map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  title={typeof option.label === 'string' ? option.label : undefined}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                      active ? 'bg-blue-50 text-blue-900' : 'text-[#232323]'
                    } ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span
                        className={`block truncate ${
                          selected ? 'font-medium' : 'font-normal'
                        }`}
                      >
                        {option.label}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </Transition>
      </div>
    </Combobox>
  );
};